import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "../ui";
import { getBillingAccessAPI } from "../../utils/api/billing";
import { useAccess } from "../../access/hooks/useAccess";
import BillingAdmin from "./admin/BillingAdmin";
import RestaurantBilling from "./RestaurantBilling";
import AccountantPage from "./accountant/AccountantPage";
import { billingAllows } from "./billingAccess";

// Which billing page opens is decided by the central policy
// (src/access/modules/billing.js). The billing API's `access` flags can only
// narrow that decision, never widen it.
const BillingEntry = () => {
  const token = localStorage.getItem("auth-token");
  const { can } = useAccess();
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // "View as Store" is a UI preview only: identity and permissions never change.
  const [previewStore, setPreviewStore] = useState(false);
  // Picking a billing section from the sidebar submenu (`?tab=`) leaves the preview.
  const [params] = useSearchParams();
  const section = params.get("tab");

  useEffect(() => {
    setPreviewStore(false);
  }, [section]);

  const loadAccess = async () => {
    setLoading(true);
    setError("");
    try {
      const result = await getBillingAccessAPI(token);
      setAccess(result.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading)
    return (
      <main className="billing-admin">
        <div className="billing-loading">Loading billing...</div>
      </main>
    );
  if (error) {
    return (
      <main className="billing-admin">
        <div className="billing-error">{error}</div>
        <Button appearance="outline" onClick={loadAccess}>
          Retry
        </Button>
      </main>
    );
  }

  const canAdmin = billingAllows(can("billing.admin.view"), access?.canAccessAdmin);
  const canClient = billingAllows(can("billing.client.view"), access?.canAccessBilling);
  const canPreview = can("billing.viewAsStore");
  const canAccountant = billingAllows(can("billing.accountant.view"), access?.canViewAccountant ?? false);

  if (canAdmin && canPreview && previewStore) {
    return <RestaurantBilling preview access={access} onExitPreview={() => setPreviewStore(false)} />;
  }
  if (canAdmin) {
    return <BillingAdmin onViewAsStore={canPreview ? () => setPreviewStore(true) : undefined} />;
  }
  if (canClient) return <RestaurantBilling access={access} />;
  // The accountant (fa) holds this workspace and no other part of Billing.
  if (canAccountant) return <AccountantPage />;
  return (
    <main className="billing-admin">
      <div className="billing-admin-denied">
        Billing is not available for this account.
      </div>
    </main>
  );
};

export default BillingEntry;
